import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, UserIcon, LayersIcon, GraduationCapIcon, BuildingIcon, PenLineIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { PageTransition } from '../components/layout/PageTransition';
import { Animate, Stagger } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
const SUBSTACK_URL = 'https://helgig.substack.com';
export function Home() {
  const {
    t
  } = useLanguage();
  const waysOfWorking = [{
    icon: <UserIcon size={28} data-id="element-1063" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    titleKey: 'home.ways.coaching.title',
    descKey: 'home.ways.coaching.desc',
    path: '/services/coaching'
  }, {
    icon: <LayersIcon size={28} data-id="element-1064" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    titleKey: 'home.ways.facilitation.title',
    descKey: 'home.ways.facilitation.desc',
    path: '/services/meetings-and-facilitation'
  }, {
    icon: <GraduationCapIcon size={28} data-id="element-1065" />,
    color: 'bg-yellow-500',
    lightColor: 'bg-yellow-100',
    textColor: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    titleKey: 'home.ways.training.title',
    descKey: 'home.ways.training.desc',
    path: '/services/courses'
  }, {
    icon: <BuildingIcon size={28} data-id="element-1066" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    titleKey: 'home.ways.embedded.title',
    descKey: 'home.ways.embedded.desc',
    path: '/services/embedded-partner'
  }];
  const writingPreviews = [{
    series: t('home.writing.series1Label'),
    title: t('home.writing.series1Title'),
    excerpt: t('home.writing.series1Excerpt'),
    href: SUBSTACK_URL,
    color: 'border-coral/30',
    labelColor: 'text-coral',
    labelBg: 'bg-coral/10'
  }, {
    series: t('home.writing.series2Label'),
    title: t('home.writing.series2Title'),
    excerpt: t('home.writing.series2Excerpt'),
    href: SUBSTACK_URL,
    color: 'border-teal/30',
    labelColor: 'text-teal',
    labelBg: 'bg-teal/10'
  }, {
    series: t('home.writing.series3Label'),
    title: t('home.writing.series3Title'),
    excerpt: t('home.writing.series3Excerpt'),
    href: SUBSTACK_URL,
    color: 'border-yellow-400/40',
    labelColor: 'text-yellow-700',
    labelBg: 'bg-yellow-100'
  }];
  return <PageTransition data-id="element-1067">
      {/* Hero */}
      <section className="relative pt-20 pb-32 overflow-hidden" data-id="element-1068">
        <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[600px] h-[600px] translate-x-1/3 -translate-y-1/4" data-id="element-1069" />
        <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-[400px] h-[400px] -translate-x-1/4 translate-y-1/4" delay={0.5} data-id="element-1070" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1071">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1072">
            <Stagger staggerDelay={0.15} data-id="element-1073">
              <div className="inline-block px-4 py-2 bg-teal/10 text-teal-dark rounded-full font-bold text-sm mb-6" data-id="element-1074">
                {t('home.hero.badge')}
              </div>
              <h1 className="md:text-6xl lg:text-7xl font-heading font-extrabold text-charcoal mb-6 text-[52px] leading-tight" data-id="element-1075">
                {t('home.hero.titleLine1')}<br data-id="element-1076" />
                <span className="text-coral" data-id="element-1077">{t('home.hero.titleLine2')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-4 leading-relaxed max-w-lg" data-id="element-1078">
                {t('home.hero.description')}
              </p>
              <p className="text-base text-gray-500 mb-8 leading-relaxed max-w-lg" data-id="element-1079">
                {t('home.hero.subDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1080">
                <Link to="/contact" data-id="element-1081">
                  <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1083" />} data-id="element-1082">
                    {t('home.hero.cta')}
                  </Button>
                </Link>
                <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-id="element-1084">
                  <Button variant="outline" size="lg" rightIcon={<ExternalLinkIcon size={16} data-id="element-1086" />} data-id="element-1085">
                    {t('home.hero.ctaWriting')}
                  </Button>
                </a>
              </div>
            </Stagger>

            <Animate delay={0.2} className="relative hidden lg:block" data-id="element-1087">
              <div className="relative w-full aspect-square max-w-md mx-auto" data-id="element-1088">
                <div className="absolute inset-0 bg-coral rounded-[3rem] rotate-6 opacity-20" data-id="element-1089" />
                <div className="absolute inset-0 bg-teal rounded-[3rem] -rotate-6 opacity-20" data-id="element-1090" />
                <img src="https://cdn.magicpatterns.com/uploads/6bt3PcPytwUVW4EyeZ9z5f/Helgi-6_A_height_x30_copy.jpg" alt="Helgi Gudmundsson" className="relative w-full h-full object-cover rounded-[3rem] shadow-2xl" loading="eager" decoding="async" fetchPriority="high" data-id="element-1091" />
                <div className="absolute -bottom-8 -left-8 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-3" data-id="element-1092">
                  <div className="bg-teal/10 p-2 rounded-full" data-id="element-1093">
                    <PenLineIcon size={22} className="text-teal" data-id="element-1094" />
                  </div>
                  <div data-id="element-1095">
                    <p className="font-bold text-charcoal text-sm" data-id="element-1096">{t('home.hero.substackBadge')}</p>
                    <p className="text-xs text-gray-500" data-id="element-1097">{t('home.hero.substackSub')}</p>
                  </div>
                </div>
              </div>
            </Animate>
          </div>
        </div>
      </section>

      {/* Writing section */}
      <section className="py-24 bg-cream relative" data-id="element-1098">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1099">
          <Animate className="mb-12" data-id="element-1100">
            <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-1101">
              {t('home.writing.sectionLabel')}
            </span>
            <h2 className="text-4xl font-bold mt-2 mb-3" data-id="element-1102">
              {t('home.writing.title')}
            </h2>
            <p className="text-gray-500 max-w-xl" data-id="element-1103">
              {t('home.writing.subtitle')}
            </p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-1104">
            {writingPreviews.map((item, i) => <Animate key={i} delay={i * 0.1} data-id="element-1105">
                <a href={item.href} target="_blank" rel="noopener noreferrer" className="block group h-full" data-id="element-1106">
                  <Card className={`h-full p-7 border-2 ${item.color} hover:shadow-md transition-all`} hoverEffect data-id="element-1107">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-4 ${item.labelBg} ${item.labelColor}`} data-id="element-1108">
                      {item.series}
                    </span>
                    <h3 className="text-lg font-bold text-charcoal mb-3 group-hover:text-coral transition-colors leading-snug" data-id="element-1109">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5" data-id="element-1110">
                      {item.excerpt}
                    </p>
                    <div className="flex items-center text-sm font-bold text-gray-400 group-hover:text-coral transition-colors" data-id="element-1111">
                      {t('home.writing.readOn')}
                      <ExternalLinkIcon size={13} className="ml-2" data-id="element-1112" />
                    </div>
                  </Card>
                </a>
              </Animate>)}
          </div>

          <Animate delay={0.35} className="text-center" data-id="element-1113">
            <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-id="element-1114">
              <Button variant="outline" rightIcon={<ExternalLinkIcon size={16} data-id="element-1116" />} data-id="element-1115">
                {t('home.writing.viewAll')}
              </Button>
            </a>
          </Animate>
        </div>
      </section>

      {/* Ways of Working */}
      <section className="py-24 relative overflow-hidden" data-id="element-1117">
        <DecorativeShape type="square" color="bg-coral/5" className="top-20 left-0 w-64 h-64 -rotate-12" data-id="element-1118" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1119">
          <Animate className="mb-12" data-id="element-1120">
            <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-1121">
              {t('home.ways.sectionLabel')}
            </span>
            <h2 className="text-4xl font-bold mt-2 mb-2" data-id="element-1122">
              {t('home.ways.title')}
            </h2>
            <p className="text-gray-500 max-w-xl" data-id="element-1123">{t('home.ways.subtitle')}</p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1124">
            {waysOfWorking.map((way, i) => <Animate key={i} delay={i * 0.1} data-id="element-1125">
                <Link to={way.path} className="block group h-full" data-id="element-1126">
                  <Card className={`h-full p-6 border-2 ${way.borderColor} hover:shadow-md transition-all`} hoverEffect data-id="element-1127">
                    <div className="flex items-start gap-4" data-id="element-1128">
                      <div className={`p-3 rounded-2xl ${way.lightColor} flex-shrink-0`} data-id="element-1129">
                        <div className={way.textColor} data-id="element-1130">{way.icon}</div>
                      </div>
                      <div className="flex-1" data-id="element-1131">
                        <h3 className={`text-lg font-bold text-charcoal mb-2 group-hover:${way.textColor} transition-colors`} data-id="element-1132">
                          {t(way.titleKey)}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-1133">
                          {t(way.descKey)}
                        </p>
                        <div className={`flex items-center ${way.textColor} text-sm font-bold`} data-id="element-1134">
                          {t('home.ways.learnMore')}
                          <ArrowRightIcon size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1135" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </Animate>)}
          </div>

          <Animate delay={0.4} className="mt-8 text-center" data-id="element-1136">
            <Link to="/services" data-id="element-1137">
              <Button variant="outline" rightIcon={<ArrowRightIcon size={16} data-id="element-1139" />} data-id="element-1138">
                {t('home.ways.viewAll')}
              </Button>
            </Link>
          </Animate>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-charcoal text-white relative overflow-hidden rounded-t-[3rem] mx-4 sm:mx-8 mb-8" data-id="element-1140">
        <DecorativeShape type="donut" color="border-white/10" className="top-0 right-0 w-96 h-96 translate-x-1/3 -translate-y-1/3" data-id="element-1141" />
        <DecorativeShape type="blob" color="bg-coral" className="bottom-0 left-0 w-64 h-64 -translate-x-1/3 translate-y-1/3 blur-3xl opacity-50" data-id="element-1142" />
        <Animate className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-1143">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1144">{t('home.cta.title')}</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-1145">{t('home.cta.description')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1146">
            <Link to="/contact" data-id="element-1147">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" data-id="element-1148">{t('home.cta.bookCall')}</Button>
            </Link>
            <Link to="/about" data-id="element-1149">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto" data-id="element-1150">{t('home.cta.readAbout')}</Button>
            </Link>
          </div>
        </Animate>
      </section>
    </PageTransition>;
}